import { useEffect, useMemo, useState } from 'react';
import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { finalize, first } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { useCitationPermissionsHook } from './use-citation-permissions.hook';
import { EditCitationView } from '@vivli/shared/features/citations/views';
import {
    useCitationsApiContext,
    useDisclosuresApiContext,
    useModalApiContext,
    useToastApiContext,
} from '@vivli/core/infrastructure/context';

const defaultCitation: ICitation = {
    doi: '',
    title: '',
    authors: [{ givenName: '', familyName: '' }],
    yearPublished: '',
    monthPublished: '',
    publisher: '',
    journal: '',
    volume: '',
    number: '',
    pages: '',
    addedByVivli: true,
    dataRequestIds: [],
    url: '',
};

interface ContextProperties {
    objectId: string;
    doiStem?: string;
    isStudy?: boolean;
    doUpdate?: boolean;
    disclosureId?: string;
    externalCitations?: ICitation[];
}

export const useCitationsContextService = ({
    objectId,
    doiStem,
    isStudy = false,
    doUpdate = false,
    disclosureId,
    externalCitations = null,
}: ContextProperties) => {
    const permissions = useCitationPermissionsHook(!!disclosureId);
    const citationsService = useCitationsApiContext();
    const disclosuresService = useDisclosuresApiContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const [citation, setCitation] = useState<ICitation>(null);
    const [isLoading, setIsLoading] = useState<boolean>(!externalCitations);
    const [citations, setCitations] = useState<ICitation[]>(externalCitations);

    const _citationsService = useMemo(() => {
        return disclosureId ? disclosuresService : citationsService;
    }, [disclosureId]);

    function setDefaultCitation() {
        setCitation({ ...defaultCitation });
    }

    const handleCitationsLoaded = (citations: ICitation[]) => {
        setIsLoading(false);

        let filteredCitations = citations;
        if (disclosureId) {
            filteredCitations = citations.filter((citation) => citation.disclosureId === disclosureId);
        }

        setCitations(filteredCitations);
    };

    function handleCreateError(err: string) {
        setIsLoading(false);
        modalService.error(`There was an error creating the citation. Please check the DOI and try again: ${err}`);
    }

    function handleCreateFromObjectError(err: string) {
        setIsLoading(false);
        modalService.error(`There was an error creating the citation. Please check the fields and try again: ${err}`);
    }

    function handleCreateCitationSuccess(citation: ICitation) {
        setDefaultCitation();
        setIsLoading(false);
        setCitations([...citations, citation]);
        toastService.success('Citation created successfully; please review the details');
    }

    const handleUpdateCitationSuccess = () => {
        citationsService
            .getCitationsForObject(objectId, isStudy, false)
            .pipe(first())
            .subscribe({
                next: (data) => {
                    setCitations(data);
                    setIsLoading(false);
                    toastService.success('Citation updated successfully');
                },
                error: handleUpdateError,
            });
    };

    function handleUpdateError(err: string) {
        setIsLoading(false);
        modalService.error(`There was an error updating the citation. Please check the fields and try again: ${err}`);
    }

    //add a new citation and on success set citation to default (empty)
    //citation so another citation can be added.
    const addCitation = (citationDoi: string, citation: ICitation) => {
        const subject = new Subject<ICitation>();

        setIsLoading(true);
        if (citationDoi) {
            _citationsService
                .createCitationFromDoi(citationDoi, objectId, doiStem, disclosureId)
                .pipe(
                    first(),
                    finalize(() => setIsLoading(false))
                )
                .subscribe({
                    next: (citation) => {
                        handleCreateCitationSuccess(citation);
                        subject.next(citation);
                    },
                    error: (err) => {
                        handleCreateError(err);
                        subject.error(err);
                    },
                });
        } else {
            _citationsService
                .createCitationFromObject(citation, objectId, doiStem, disclosureId)
                .pipe(
                    first(),
                    finalize(() => setIsLoading(false))
                )
                .subscribe({
                    next: (citation) => {
                        handleCreateCitationSuccess(citation);
                        subject.next(citation);
                    },
                    error: (err) => {
                        handleCreateFromObjectError(err);
                        subject.error(err);
                    },
                });
        }

        return subject;
    };

    const updateCitation = (citation: ICitation) => {
        modalService.custom(
            <EditCitationView
                citation={citation}
                onCancel={() => modalService.dismiss()}
                onSave={(updatedCitation) => {
                    const subject = new Subject<void>();

                    saveUpdatedCitation(updatedCitation)
                        .pipe(finalize(() => setIsLoading(false)))
                        .subscribe({
                            next: () => {
                                toastService.success('Citation updated successfully.');
                                modalService.dismiss();
                            },
                            error: subject.error,
                        });

                    return subject;
                }}
            />
        );
    };

    const saveUpdatedCitation = (citation: ICitation) => {
        const subject = new Subject<ICitation>();

        setIsLoading(true);
        _citationsService
            .updateCitation(citation, citation.id, doiStem)
            .pipe(first())
            .subscribe({
                next: (citation) => {
                    handleUpdateCitationSuccess();
                    subject.next(citation);
                },
                error: handleUpdateError,
            });

        return subject;
    };

    const removeCitation = (id: string) => {
        modalService.confirm('Are you sure you want to remove this citation?', {
            onConfirm: () => {
                setIsLoading(true);
                _citationsService
                    .deleteCitation(id, objectId, doiStem)
                    .pipe(
                        first(),
                        finalize(() => setIsLoading(false))
                    )
                    .subscribe({
                        next: () => {
                            const newCitations = citations.filter((c) => c.id !== id);
                            setCitations(newCitations);
                            toastService.success(`Citation deleted successfully from Data Request ${doiStem}`);
                        },
                        error: (err) => {
                            modalService.error(`There was an error deleting the citation: ${err}`);
                        },
                    });
            },
        });
    };

    const getCitationsError = () => {
        setIsLoading(false);
        setCitations([]); // set citations to empty so the grid shows there are none found
        // this error has no cause anymore but the error message is occasionally triggered anyway
        /*modalService.error(
            'An error was encountered while fetching citations.  Please report the ID of the study to Vivli at support@vivli.org'
        );*/
    };

    useEffect(() => {
        if (!objectId || externalCitations?.length > 0) {
            return;
        }

        if (permissions.user) {
            //get citations
            citationsService.getCitationsForObject(objectId, isStudy, doUpdate).pipe(first()).subscribe({
                next: handleCitationsLoaded,
                error: getCitationsError,
            });
        } else {
            //anonymous access allowed without logged in user
            citationsService.getCitationsForObjectAnonymous(objectId, isStudy, doUpdate).pipe(first()).subscribe({
                next: handleCitationsLoaded,
                error: getCitationsError,
            });
        }
    }, [objectId]);

    useEffect(() => {
        if (!citation) {
            setDefaultCitation();
        } else {
            setCitation(citation);
        }
    }, []);

    return {
        citation,
        isLoading,
        setIsLoading,
        isStudy,
        citations,
        setCitation,
        setCitations,
        addCitation,
        removeCitation,
        updateCitation,
        defaultCitation,
        permissions,
        objectId,
        // only show the links when this is not being used for disclosures
        showDisclosureLinks: !disclosureId,
    };
};
